import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Badge } from "#components/atoms/Badge/Badge";
import { Divider } from "#components/atoms/Divider/Divider";
import { Loader } from "#components/atoms/Loader/Loader";
import { Text } from "#components/atoms/Typographies/Text";
import { getNestingItemColors } from "#modules/layoutsV2/domain/getNestingItemColors";
import { formatPartDimensions } from "#modules/parts/domain/formatPartDimensions";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { Flex } from "#shared/view/components/Flex";
import { Thumbnail } from "#shared/view/components/Thumbnails/Thumbnail";
import { formatPrice, PartTypeSchema } from "shared";

export type NestingItem = {
  id: string;
  part: QuoteRequestLineDto["part"];
  quantity: number;
  unitMaterialCost: number | null;
};

type Props = {
  items: NestingItem[];
  isMaterialCostLoading: boolean;
  hasItemColors: boolean;
  renderRowAction?: (itemId: string) => ReactNode;
};

const QUANTITY_COLUMN_WIDTH = 71;
const COST_COLUMN_WIDTH = 142;
const THUMBNAIL_SIZE = 48;
const ITEM_NUMBER_SIZE = 20;

export const NestingItemsTable = ({
  items,
  isMaterialCostLoading,
  hasItemColors,
  renderRowAction,
}: Props) => {
  const { t } = useTranslation();
  const hasSeveralItems = items.length > 1;

  return (
    <Flex direction="column">
      <Flex align="center" gap={spacing[4]} p={spacing[2]}>
        <Text size="small" type="medium" style={{ flex: 1 }}>
          {t("layout.sidePanel.table.part")}
        </Text>
        <Text size="small" type="medium" textAlign="right" style={{ width: QUANTITY_COLUMN_WIDTH }}>
          {t("layout.sidePanel.table.quantity")}
        </Text>
        <Text size="small" type="medium" textAlign="right">
          {t("layout.sidePanel.table.unitMaterialCost")}
        </Text>
      </Flex>
      <Divider />
      {items.map((item, index) => (
        <NestingItemRow
          key={item.id}
          item={item}
          number={hasSeveralItems ? index + 1 : null}
          hasColor={hasItemColors}
          isMaterialCostLoading={isMaterialCostLoading}
          rowAction={renderRowAction?.(item.id)}
        />
      ))}
    </Flex>
  );
};

type NestingItemRowProps = {
  item: NestingItem;
  number: number | null;
  hasColor: boolean;
  isMaterialCostLoading: boolean;
  rowAction?: ReactNode;
};

const NestingItemRow = ({
  item,
  number,
  hasColor,
  isMaterialCostLoading,
  rowAction,
}: NestingItemRowProps) => {
  const { t } = useTranslation();
  const { part, quantity, unitMaterialCost } = item;

  return (
    <>
      <Flex align="center" gap={spacing[4]} p={spacing[2]}>
        <Flex align="center" gap={spacing[4]} flex={1} style={{ minWidth: 0 }}>
          {number === null ? null : <NestingItemNumber number={number} hasColor={hasColor} />}
          <Thumbnail part={part} size={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE }} />
          <Flex direction="column" gap={spacing[0.5]} style={{ minWidth: 0 }}>
            <Text size="small" type="medium" truncate>
              {part.name}
            </Text>
            <Flex align="center" gap={spacing[2]}>
              <Badge variant="neutral">
                {t(`part.type.${part.type ?? PartTypeSchema.enum.OTHER}`)}
              </Badge>
              <Text size="tiny" color={colors.text.secondaryDefault} truncate>
                {formatPartDimensions(part)}
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <Text size="small" textAlign="right" style={{ width: QUANTITY_COLUMN_WIDTH }}>
          {quantity}
        </Text>
        <Flex justify="flex-end" style={{ width: COST_COLUMN_WIDTH }}>
          {isMaterialCostLoading ? (
            <Loader size="small" />
          ) : (
            <Text size="small" textAlign="right">
              {unitMaterialCost === null ? "–" : formatPrice(unitMaterialCost)}
            </Text>
          )}
        </Flex>
        {rowAction}
      </Flex>
      <Divider />
    </>
  );
};

const NEUTRAL_ITEM_NUMBER_COLORS = {
  background: colors.bg.elevation.quaternary,
  text: colors.text.primaryDefault,
};

const NestingItemNumber = ({ number, hasColor }: { number: number; hasColor: boolean }) => {
  const { background, text } = hasColor
    ? getNestingItemColors(number - 1)
    : NEUTRAL_ITEM_NUMBER_COLORS;

  return (
    <Flex
      align="center"
      justify="center"
      style={{
        flexShrink: 0,
        width: ITEM_NUMBER_SIZE,
        height: ITEM_NUMBER_SIZE,
        borderRadius: radius.tiny,
        backgroundColor: background,
      }}
    >
      <Text size="tiny" type="medium" color={text}>
        {number}
      </Text>
    </Flex>
  );
};
